import React, { useEffect, useState } from "react";

/**
 * Animated banner carousel — cycles through 7 branded banners showcasing every
 * angle of SUX1 (brand, models, formats, audience, pricing, features, trust).
 *
 * Design language: split layout, blue #002FA7 wordmark panel on the left,
 * white content panel on the right, diagonal red #FF2A00 separator.
 * Zero external images or dependencies — pure CSS animation.
 */

// Each banner has:
//   left  → { top: heading, mid: main wordmark line, bottom: tagline }
//   right → { title: big H1 (split by \n), items: bullet list }
const BANNERS = [
  {
    id: "brand",
    left: {
      top: "01 · MARQUE",
      wordmark: "SUX1",
      tagline: ["DISTILL ANY TEXT.", "FAST. ACCURATE. CHEAP."],
    },
    right: {
      title: ["Résumé instantané", "par IA."],
      items: [
        "Claude · GPT · Gemini",
        "PDF · DOCX · URL · texte",
        "Mode chapitre pour livres",
        "Exemples gratuits — puis dès 2 € les 20",
      ],
    },
  },
  {
    id: "models",
    left: {
      top: "02 · 3 IA",
      wordmark: "3 IA.",
      tagline: ["UN SEUL", "OUTIL."],
    },
    right: {
      title: ["3 modèles IA", "au choix."],
      items: [
        "Claude Sonnet 4.5 — précision littéraire",
        "GPT-5.2 — équilibre & polyvalence",
        "Gemini 3 Flash — 10 s chrono",
        "Basculez d'un modèle à l'autre à volonté",
      ],
    },
  },
  {
    id: "formats",
    left: {
      top: "03 · FORMATS",
      wordmark: "TOUT.",
      tagline: ["PDF · DOCX · EPUB", "IMAGE · URL · TEXTE"],
    },
    right: {
      title: ["Tous les formats,", "un seul outil."],
      items: [
        "PDF jusqu'à 800 pages · découpé par chapitres",
        "DOCX, EPUB, images (OCR intégré)",
        "URL — articles web scrapés en 3 secondes",
        "Photo de couverture → fiche de lecture",
      ],
    },
  },
  {
    id: "audience",
    left: {
      top: "04 · POUR QUI",
      wordmark: "VOUS.",
      tagline: ["ÉTUDIANTS", "ENSEIGNANTS"],
    },
    right: {
      title: ["L'outil de ceux", "qui doivent lire vite."],
      items: [
        "Étudiants — révisions, mémoires, examens",
        "Enseignants — préparation de cours, dossiers",
        "Chercheurs — synthèses d'articles scientifiques",
        "Journalistes — dépêches et rapports en 30 s",
      ],
    },
  },
  {
    id: "pricing",
    left: {
      top: "05 · PRIX",
      wordmark: "2 €.",
      tagline: ["20 RÉSUMÉS.", "SANS ABONNEMENT."],
    },
    right: {
      title: ["Deux euros.", "20 résumés, sans expiration."],
      items: [
        "Dès 2 € les 20 résumés — sans abonnement",
        "Puis dès 2 € les 20 résumés, crédits sans expiration",
        "Packs de 60 (5 €) ou 130 résumés (10 €)",
        "Pas d'abonnement · pas de reconduction",
      ],
    },
  },
  {
    id: "features",
    left: {
      top: "06 · UNIQUE",
      wordmark: "PLUS.",
      tagline: ["4 REGISTRES", "CACHE HORS LIGNE"],
    },
    right: {
      title: ["Ce que personne d'autre", "ne fait."],
      items: [
        "4 registres de langue (soutenu → familier)",
        "Historique lisible hors ligne (train · avion)",
        "Scan d'un livre par photo",
        "Retravailler · Développer un résumé en 1 clic",
      ],
    },
  },
  {
    id: "trust",
    left: {
      top: "07 · CONFIANCE",
      wordmark: "RGPD.",
      tagline: ["MADE IN", "FRANCE"],
    },
    right: {
      title: ["Vos données", "restent chez vous."],
      items: [
        "Aucun stockage permanent des documents",
        "Hébergement en Europe · RGPD conforme",
        "Suppression du compte en un clic",
        "Application indépendante · Made in France",
      ],
    },
  },
];

export function BannerCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-cycle every 10 s — enough time for a reader to comfortably scan
  // the 2-line title + 4 bullets without feeling rushed. Pauses on hover.
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % BANNERS.length), 10000);
    return () => clearInterval(id);
  }, [paused]);

  const b = BANNERS[idx];

  return (
    <div className="w-full" data-testid="banner-carousel">
      <style>{`
        @keyframes sux1b-fade-up { from { opacity: 0; transform: translateY(40px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes sux1b-fade-left { from { opacity: 0; transform: translateX(-40px);} to { opacity: 1; transform: translateX(0);} }
        @keyframes sux1b-wipe {
          0%   { transform: translateX(-100%); }
          40%  { transform: translateX(0); }
          60%  { transform: translateX(0); }
          100% { transform: translateX(100%); }
        }
        @keyframes sux1b-scale-in { from { opacity: 0; transform: scale(0.85);} to { opacity: 1; transform: scale(1);} }
        .sux1b-fade-up   { animation: sux1b-fade-up 0.9s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .sux1b-fade-left { animation: sux1b-fade-left 0.9s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .sux1b-scale-in  { animation: sux1b-scale-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .sux1b-delay-1   { animation-delay: 0.15s; }
        .sux1b-delay-2   { animation-delay: 0.30s; }
        .sux1b-delay-3   { animation-delay: 0.45s; }
        .sux1b-delay-4   { animation-delay: 0.60s; }
        .sux1b-wipe {
          position: absolute; inset: 0; background: #FF2A00;
          animation: sux1b-wipe 1.0s cubic-bezier(0.87, 0, 0.13, 1) both;
          pointer-events: none; z-index: 20;
        }
      `}</style>

      {/* Banner */}
      <div
        key={b.id}
        data-testid={`banner-${b.id}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="relative overflow-hidden grid grid-cols-1 md:grid-cols-2 min-h-[380px] md:min-h-[440px] border border-border"
      >
        {/* Wipe rouge au changement de bannière */}
        <div className="sux1b-wipe" aria-hidden="true" />
        {/* LEFT — blue panel with big wordmark */}
        <div className="bg-[#002FA7] text-white flex flex-col justify-center p-8 md:p-12 relative">
          <div className="sux1b-fade-left font-mono uppercase tracking-[0.25em] text-[10px] text-[#8BA1E8] mb-4">
            {b.left.top}
          </div>
          <div
            className="sux1b-fade-left sux1b-delay-1 font-black leading-[0.9] mb-6"
            style={{
              fontFamily: "Outfit",
              fontSize: "clamp(72px, 12vw, 148px)",
              letterSpacing: "-0.03em",
            }}
          >
            {b.left.wordmark}
          </div>
          {b.left.tagline.map((line, i) => (
            <div
              key={i}
              className={`sux1b-fade-left sux1b-delay-${i + 2} font-mono uppercase tracking-[0.14em] text-sm md:text-base text-[#B7C4EF] leading-relaxed`}
            >
              {line}
            </div>
          ))}
          {/* Language tag top-right */}
          <div className="absolute top-6 right-6 font-mono uppercase tracking-[0.16em] text-[10px] bg-[#002FA7] text-white px-3 py-1.5">
            FR · EN · PWA
          </div>
        </div>

        {/* Diagonal red separator (desktop only) */}
        <div
          aria-hidden="true"
          className="hidden md:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 pointer-events-none z-10"
          style={{
            width: "10px",
            background: "#FF2A00",
            transform: "translateX(-50%) skewX(-14deg)",
          }}
        />

        {/* RIGHT — white content panel */}
        <div className="bg-background flex flex-col justify-center p-8 md:p-12 md:pl-16">
          <h2
            className="sux1b-scale-in font-black leading-[1.05] mb-8 text-foreground"
            style={{
              fontFamily: "Outfit",
              fontSize: "clamp(28px, 4vw, 48px)",
              letterSpacing: "-0.02em",
            }}
          >
            {b.right.title.map((line, i) => (
              <span
                key={i}
                className={i === 1 ? "text-[#FF2A00] block" : "block"}
              >
                {line}
              </span>
            ))}
          </h2>
          <ul className="space-y-3.5">
            {b.right.items.map((it, i) => (
              <li
                key={i}
                className={`sux1b-fade-up sux1b-delay-${i + 1} flex items-start gap-3 text-foreground text-[15px] md:text-base leading-snug`}
              >
                <span className="mt-1.5 w-3 h-3 bg-[#002FA7] shrink-0" aria-hidden="true" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Progress dots */}
      <div className="mt-6 flex items-center justify-center gap-2" data-testid="banner-progress-dots">
        {BANNERS.map((banner, i) => (
          <button
            key={banner.id}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`banner-dot-${banner.id}`}
            aria-label={`Bannière ${i + 1} sur ${BANNERS.length}`}
            className={`h-1.5 transition-all ${
              i === idx ? "w-10 bg-[#002FA7]" : "w-3 bg-border hover:bg-[#FF2A00]/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default BannerCarousel;
